import type {
  HeadlineResponse,
  HeadlineResponsePagedData,
  LatestForumPostsResponse,
  ScoreboardResponseRelatedScheduleResponseRelatedPagedData,
  TrendingListItemResponseTrendingListResponseRelatedPagedData,
} from '@on3/api';
import { Link } from '@on3/ui-lib/components/Link';
import { useSite } from '@on3/ui-lib/index';
import { MPU } from 'components/Ads/MPU';
import { PostFeed } from 'components/Forums/Forums';
import { CondensedList } from 'components/Headlines/ArticleCondensed/CondensedList';
import { ArticlesList } from 'components/Headlines/ArticleList';
import { SiteLogo } from 'components/Image/SiteLogo';
import { HomeLayout } from 'components/Layouts/HomeLayout';
import { ScheduleModule } from 'components/Schedules/Sidebar/Module';
import { TeamConnect } from 'components/Social/TeamConnect';
import { PodcastPlayer } from 'components/Teams/Podcast/PodcastPlayer';
import { TopHeadlines } from 'components/TopHeadlines';
import { getArticleLists } from 'utils/articles';
import { createTitle } from 'utils/meta';

import { SEOHomeText } from '../SEOHomeText';
import styles from './FeedHome.module.scss';

export interface IFeedHomeProps {
  feedArticles: HeadlineResponsePagedData | null;
  featuredArticles: HeadlineResponse[] | null;
  featuredArticlesSecondary: HeadlineResponse[] | null;
  siteForums: LatestForumPostsResponse | null;
  schedule: ScoreboardResponseRelatedScheduleResponseRelatedPagedData | null;
  trendingList:
    | TrendingListItemResponseTrendingListResponseRelatedPagedData[]
    | TrendingListItemResponseTrendingListResponseRelatedPagedData
    | null;
}

const podcastData = {
  allHref:
    '/teams/kentucky-wildcats/news/your-guide-on-how-to-listen-to-the-ksr-podcast-network/',
  src: 'https://playlist.megaphone.fm/?p=LRL4571054944',
};

export const FeedHome = ({
  feedArticles,
  featuredArticles,
  featuredArticlesSecondary,
  siteForums,
  schedule,
  trendingList,
}: IFeedHomeProps) => {
  const { currentSite, siblingSite } = useSite();

  const isKsrMinus = currentSite?.url === '/teams/kentucky-wildcats';

  const [_, featuredArticlesList, featuredArticlesSecondaryList] =
    getArticleLists({
      featuredArticles: {
        list: [],
      },
      lists: [
        {
          list: featuredArticles ?? [],
          sliceLimit: 5,
          removeDuplicates: true,
        },
        {
          list: featuredArticlesSecondary ?? [],
          sliceLimit: 5,
          removeDuplicates: true,
        },
      ],
    });

  const forumsLinks = [
    {
      title: 'All Topics',
      text: 'All Topics',
      url: siteForums?.primaryForum?.url ?? '',
    },
    {
      title: 'All Forums',
      text: 'All Forums',
      url: siteForums?.url ?? '',
    },
  ].filter((l) => !!l.url);

  return (
    <>
      <div className={styles.ksrBanner}>
        <div className={styles.inner}>
          <Link href={`${currentSite?.url}/`}>
            <SiteLogo className={styles.logo} height={50} />
          </Link>
        </div>
      </div>
      <HomeLayout className={styles.layout}>
        <section className={styles.center}>
          <div className={styles.inner}>
            <SEOHomeText className={styles.tagline} />

            {feedArticles?.list?.length && (
              <ArticlesList
                list={feedArticles.list}
                pagination={feedArticles.pagination}
              />
            )}
          </div>
        </section>
        <aside className={styles.right}>
          <div className={styles.inner}>
            <MPU id="mpu_top" />

            {!!siteForums?.posts?.length && (
              <PostFeed
                forum={siteForums.primaryForum}
                isModule
                links={forumsLinks}
                posts={siteForums.posts}
                title="Popular Message Board Posts"
              />
            )}

            {!!schedule?.list?.length && (
              <ScheduleModule
                list={schedule?.list}
                relatedModel={schedule?.relatedModel}
              />
            )}

            <MPU id="mpu_middle" />
          </div>
        </aside>
        <aside className={styles.left}>
          <div className={styles.inner}>
            {!!trendingList && <TopHeadlines trendingList={trendingList} />}

            {(!!featuredArticlesList.length ||
              !!featuredArticlesSecondaryList.length) && (
              <CondensedList
                list={featuredArticlesList}
                listLink={
                  isKsrMinus ? '/sites/kentucky-wildcats/news/' : undefined
                }
                listTitle={createTitle(
                  isKsrMinus ? 'KSR+' : siblingSite?.siteName,
                  'News',
                )}
                secondaryList={featuredArticlesSecondaryList}
                secondaryListLink={
                  isKsrMinus ? '/college/kentucky-wildcats/news/' : undefined
                }
                secondaryListTitle={createTitle(
                  siblingSite?.organization?.mascot,
                  'News',
                )}
              />
            )}

            <PodcastPlayer data={podcastData} />

            <div className={styles.sticky}>
              <TeamConnect />
            </div>
          </div>
        </aside>
      </HomeLayout>
    </>
  );
};
